import { useId } from 'react';
import styled from 'styled-components';
import Signature, { SignatureRef } from '@uiw/react-signature';
import { standardTransitionStyles } from '../../sharedStyles';
import Button from '../Button/Button';
import { FormLabel } from '../FormLabel/FormLabel';

export type DrawingInputFieldRef = SignatureRef;

const BORDER_RADIUS = 'var(--border-radius-medium)';

const StyledDrawInputField = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  border: 1px solid var(--grayscale-100);
  border-bottom: none;
  border-radius: ${BORDER_RADIUS} ${BORDER_RADIUS} 0 0;
  background-color: var(--surface-200);
  padding: 8px;
`;

const CanvasContainer = styled.div<{ $hasError?: boolean; $aspectRatio?: string }>`
  border: 1px solid var(${({ $hasError }) => ($hasError ? '--red-400' : '--grayscale-100')});
  background-color: var(--surface-100);
  // top corners are sharp
  border-radius: 0 0 ${BORDER_RADIUS} ${BORDER_RADIUS};
  width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  box-sizing: border-box;
  position: relative;
  ${standardTransitionStyles}

  &:hover {
    background-color: var(--surface-50);
    cursor: pointer;
  }
`;

const StyledSignature = styled(Signature)<{ $aspectRatio?: string }>`
  // override the library's definition for this css variable
  // https://uiwjs.github.io/react-signature/#background-color--text-color
  --w-signature-background: transparent !important;
  width: 100%;
  height: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
`;

const SignatureLine = styled.div<{ $hasError?: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 24px;
  height: 1px;
  background-color: var(${({ $hasError }) => ($hasError ? '--red-400' : '--grayscale-100')});
  ${standardTransitionStyles}
`;

const ClearButton = styled(Button)`
  background-color: var(--white);

  &:hover {
    background-color: var(--white);
    border-color: var(--grayscale-100);
  }
`;

export type DrawInputFieldProps = {
  label: string;
  // If passed, the label will not be visually displayed.
  hideLabel?: boolean;
  hasError?: boolean;
  drawingInputFieldRef: React.RefObject<DrawingInputFieldRef>;
  // data is the points for the line just created.
  // this method is also called when the drawing rerenders, so it may sometimes return an empty array if it's
  // not responding to a new line being drawn.
  onDrawingChange: (data: number[][]) => void;
  onClearDrawing: () => void;
  // should be in the format "width / height", e.g. "5 / 2"
  // NOTE: the minimum height of the drawing area is 155px, regardless of the aspect ratio.
  //       this size is defined in the underlying library's logic
  aspectRatio?: string;
  className?: string;
};

/**
 * An input field that allows the user to draw something.
 *
 * Includes a clear button to remove the drawing but no utilities around color or size of the cursor when drawing.
 *
 * Built on top of React Signature https://uiwjs.github.io/react-signature/
 */
export const DrawInputField = ({
  label,
  hasError,
  hideLabel = false,
  drawingInputFieldRef,
  onDrawingChange,
  onClearDrawing,
  aspectRatio = '5 / 2',
  className,
}: DrawInputFieldProps) => {
  const labelId = useId();
  const handleClear = () => {
    drawingInputFieldRef.current?.clear();
    onClearDrawing();
  };

  return (
    <StyledDrawInputField className={className}>
      <FormLabel id={labelId} className={hideLabel ? 'sr-only' : ''}>
        {label}
      </FormLabel>

      <InputContainer>
        <Toolbar>
          <ClearButton onClick={handleClear} size="sm" variant="tertiary">
            Clear
          </ClearButton>
        </Toolbar>

        <CanvasContainer $hasError={hasError} $aspectRatio={aspectRatio}>
          <SignatureLine $hasError={hasError} />

          <StyledSignature ref={drawingInputFieldRef} onPointer={onDrawingChange} $aspectRatio={aspectRatio} />
        </CanvasContainer>
      </InputContainer>
    </StyledDrawInputField>
  );
};

export default DrawInputField;
